import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { Json } from "@obsn/shared/db";
import { db } from "../../kern/supabase";
import type { Aufgabe, KatalogArt, Prioritaet, Sichtbarkeit, Status } from "./logik";
import { ressortFarben } from "./listenlogik";

/* Alle Spalten der View. So läuft die Liste auch in den Minuten zwischen Merge und Einspielen der Migration, in denen es
   die Spalte personen noch nicht gibt (dann gilt person als einzige Verantwortliche). */
const SPALTEN = "*";

type Zeile = Record<string, unknown>;

/** Zeile der View aufgaben_liste in eine Aufgabe mit festen Typen (Views liefern alle Spalten als nullable). */
export function alsAufgabe(z: Zeile): Aufgabe {
  const links = Array.isArray(z.links) ? (z.links as unknown[]).filter((l): l is { label: string; url: string } => !!l && typeof l === "object" && typeof (l as { url?: unknown }).url === "string") : [];
  return {
    id: String(z.id),
    titel: String(z.titel ?? ""),
    beschreibung: String(z.beschreibung ?? ""),
    eltern_id: (z.eltern_id as string | null) ?? null,
    eltern_titel: (z.eltern_titel as string | null) ?? null,
    person: (z.person as string | null) ?? null,
    personen: Array.isArray(z.personen) ? (z.personen as unknown[]).filter((x): x is string => typeof x === "string") : z.person ? [String(z.person)] : [],
    status: (z.status as Status) ?? "zu_tun",
    status_grund: String(z.status_grund ?? ""),
    prioritaet: (z.prioritaet as Prioritaet) ?? "normal",
    frist_datum: (z.frist_datum as string | null) ?? null,
    frist: (z.frist as string | null) ?? null,
    sichtbarkeit: (z.sichtbarkeit as Sichtbarkeit) ?? "vorstand",
    ressort_id: (z.ressort_id as string | null) ?? null,
    vorhaben_id: (z.vorhaben_id as string | null) ?? null,
    links: links.map((l) => ({ label: String(l.label ?? l.url), url: l.url })),
    herkunft: String(z.herkunft ?? ""),
    erledigt_am: (z.erledigt_am as string | null) ?? null,
    archiviert_am: (z.archiviert_am as string | null) ?? null,
    erstellt_von: (z.erstellt_von as string | null) ?? null,
    erstellt_am: String(z.erstellt_am ?? ""),
    sprint_id: (z.sprint_id as string | null) ?? null,
    sprint_name: (z.sprint_name as string | null) ?? null,
    sprint_status: (z.sprint_status as Aufgabe["sprint_status"]) ?? null,
    ueberfaellig: z.ueberfaellig === true,
    unteraufgaben_offen: Number(z.unteraufgaben_offen ?? 0),
    unteraufgaben_gesamt: Number(z.unteraufgaben_gesamt ?? 0),
    // Vor der Migration vom 13.10. fehlen diese Spalten: dann gilt das eigene Datum, ohne Sprintende
    frist_gilt: (z.frist_gilt as string | null | undefined) ?? (z.frist_datum as string | null) ?? null,
    geaendert_von: (z.geaendert_von as string | null) ?? null,
    kommentare: Number(z.kommentare ?? 0),
  };
}

/** Alle sichtbaren Aufgaben (RLS entscheidet). Neuladen alle 15 Sekunden. Für einen kleinen Verein reicht eine Liste. */
export function useAufgaben(mitArchiv = false) {
  return useQuery({
    queryKey: ["aufgaben", { mitArchiv }],
    refetchInterval: 15_000,
    queryFn: async (): Promise<Aufgabe[]> => {
      let q = db().from("aufgaben_liste").select(SPALTEN).order("erstellt_am", { ascending: false }).limit(1000);
      if (!mitArchiv) q = q.is("archiviert_am", null);
      const { data, error } = await q;
      if (error) throw error;
      return (data as Zeile[]).map(alsAufgabe);
    },
  });
}

export function useAufgabe(id: string | undefined) {
  return useQuery({
    queryKey: ["aufgabe", id],
    enabled: !!id,
    refetchInterval: 15_000,
    queryFn: async () => {
      const [a, u] = await Promise.all([
        db().from("aufgaben_liste").select(SPALTEN).eq("id", id ?? "").maybeSingle(),
        db().from("aufgaben_liste").select(SPALTEN).eq("eltern_id", id ?? "").is("archiviert_am", null).order("erstellt_am"),
      ]);
      if (a.error) throw a.error;
      if (u.error) throw u.error;
      return { aufgabe: a.data ? alsAufgabe(a.data as Zeile) : null, unteraufgaben: (u.data as Zeile[]).map(alsAufgabe) };
    },
  });
}

/** Verlauf einer Aufgabe (app_ereignisse), älteste zuerst. Neu alle 30 Sekunden, solange der Dialog offen ist. */
export function useVerlauf(id: string | undefined) {
  return useQuery({
    queryKey: ["verlauf", id],
    enabled: !!id,
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await db()
        .from("app_ereignisse")
        .select("id, typ, person, daten, erstellt_am")
        .eq("objekt_typ", "aufgabe")
        .eq("objekt_id", id ?? "")
        .order("erstellt_am", { ascending: true })
        .limit(200);
      if (error) throw error;
      return data;
    },
  });
}

export interface Kommentar {
  id: string;
  person: string | null;
  text: string;
  erwaehnt: string[];
  erstellt_am: string;
  geloescht_am: string | null;
}

/** Kommentare einer Aufgabe, älteste zuerst. Neu alle 30 Sekunden, solange der Dialog offen ist. Kein Realtime. */
export function useKommentare(id: string | undefined) {
  return useQuery({
    queryKey: ["kommentare", id],
    enabled: !!id,
    refetchInterval: 30_000,
    queryFn: async (): Promise<Kommentar[]> => {
      const { data, error } = await db()
        .from("aufgaben_kommentare")
        .select("id, person, text, erwaehnt, erstellt_am, geloescht_am")
        .eq("aufgabe", id ?? "")
        .order("erstellt_am", { ascending: true });
      if (error) throw error;
      return data;
    },
  });
}

type Aenderung = Partial<Pick<Aufgabe, "titel" | "beschreibung" | "prioritaet" | "frist_datum" | "frist" | "ressort_id" | "vorhaben_id" | "sichtbarkeit" | "links" | "status" | "status_grund" | "personen">>;

/** Optimistisch: Aufgabe in allen geladenen Listen und im Dialog sofort ändern. Liefert die alten Daten zum Zurückrollen. */
function sofortAendern(qc: QueryClient, id: string, aenderung: (a: Aufgabe) => Aenderung) {
  const alt = [...qc.getQueriesData({ queryKey: ["aufgaben"] }), ...qc.getQueriesData({ queryKey: ["aufgabe"] })];
  const patch = (a: Aufgabe): Aufgabe => {
    if (a.id !== id) return a;
    const n = { ...a, ...aenderung(a) };
    if (n.personen) n.person = n.personen[0] ?? null;
    if ("frist_datum" in aenderung(a)) n.frist = n.frist_datum;
    return n;
  };
  qc.setQueriesData<Aufgabe[]>({ queryKey: ["aufgaben"] }, (liste) => liste?.map(patch));
  qc.setQueriesData<{ aufgabe: Aufgabe | null; unteraufgaben: Aufgabe[] }>({ queryKey: ["aufgabe"] }, (d) =>
    d ? { aufgabe: d.aufgabe ? patch(d.aufgabe) : null, unteraufgaben: d.unteraufgaben.map(patch) } : d,
  );
  return () => alt.forEach(([key, daten]) => qc.setQueryData(key, daten));
}

export function useFolge(id: string | undefined) {
  return useQuery({
    queryKey: ["folgen", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await db().from("aufgaben_folgen").select("aufgabe").eq("aufgabe", id ?? "");
      if (error) throw error;
      return data.length > 0;
    },
  });
}

export function useRessorts() {
  return useQuery({
    queryKey: ["ressorts"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await db().from("aufgaben_ressorts").select("id, name, aktiv").eq("aktiv", true).order("name");
      if (error) throw error;
      return data;
    },
  });
}

/** Farben der Ressorts nach Reihenfolge des Anlegens, auch inaktive (AUFGABEN-LISTE.md, Abschnitt 3.3) */
export function useRessortFarben() {
  return useQuery({
    queryKey: ["ressorts", "farben"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await db().from("aufgaben_ressorts").select("id, erstellt_am");
      if (error) throw error;
      return ressortFarben(data);
    },
  });
}

export function useVorhaben() {
  return useQuery({
    queryKey: ["vorhaben"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await db().from("aufgaben_vorhaben").select("id, name, ressort_id, aktiv").eq("aktiv", true).order("name");
      if (error) throw error;
      return data;
    },
  });
}

/** Neues Ressort oder Vorhaben über aufgaben_ressort_anlegen bzw. aufgaben_vorhaben_anlegen (Ressort mit sprint.planen, Vorhaben mit vorhaben.verwalten).
 *  Gibt es den Namen schon, liefert die Datenbank dessen id und schaltet ihn wieder aktiv. */
export function useKatalogAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ art, name, ressort }: { art: KatalogArt; name: string; ressort: string | null }): Promise<string> => {
      const { data, error } =
        art === "ressort"
          ? await db().rpc("aufgaben_ressort_anlegen", { p_name: name })
          : await db().rpc("aufgaben_vorhaben_anlegen", { p_name: name, p_ressort: ressort ?? undefined });
      if (error) throw error;
      return data;
    },
    onSuccess: (_id, { art }) => void qc.invalidateQueries({ queryKey: [art === "ressort" ? "ressorts" : "vorhaben"] }),
  });
}

export interface NeueAufgabe {
  titel: string;
  person: string | null;
  /** Mehrere Verantwortliche (Stufe 2). Leer: wie person. */
  personen?: string[];
  /** Laufender Sprint oder null (Backlog). Ohne sprint.planen legt die Datenbank immer im Backlog an. */
  sprint: string | null;
  frist_datum: string | null;
  prioritaet: Prioritaet;
  sichtbarkeit: Sichtbarkeit;
  eltern: string | null;
  ressort: string | null;
  vorhaben: string | null;
  beschreibung?: string;
}

/** Alle Schreibvorgänge laufen über RPCs (ADR-002). Nach jeder Änderung werden Listen neu geladen. */
export function useAufgabenAktionen() {
  const qc = useQueryClient();
  const neu = () => {
    void qc.invalidateQueries({ queryKey: ["aufgaben"] });
    void qc.invalidateQueries({ queryKey: ["aufgabe"] });
    void qc.invalidateQueries({ queryKey: ["verlauf"] });
    void qc.invalidateQueries({ queryKey: ["mitteilungen"] });
    void qc.invalidateQueries({ queryKey: ["sprint-ergebnisse"] });
    void qc.invalidateQueries({ queryKey: ["kommentare"] });
    // Zahlen auf den Karten der Vorhaben
    void qc.invalidateQueries({ queryKey: ["vorhaben", "uebersicht"] });
  };
  /** Sofort anzeigen, bei Fehler zurück (die Meldung zeigt der Aufrufer über onError). */
  const sofort = (id: string, aenderung: (a: Aufgabe) => Aenderung) => {
    void qc.cancelQueries({ queryKey: ["aufgaben"] });
    void qc.cancelQueries({ queryKey: ["aufgabe"] });
    return { zurueck: sofortAendern(qc, id, aenderung) };
  };
  const zurueck = (_e: unknown, _v: unknown, ctx: { zurueck: () => void } | undefined) => ctx?.zurueck();
  const rpc = async (fn: PromiseLike<{ error: unknown }>) => {
    const { error } = await fn;
    if (error) throw error;
  };
  return {
    anlegen: useMutation({
      mutationFn: async (n: NeueAufgabe): Promise<string> => {
        const { data, error } = await db().rpc("aufgaben_anlegen", {
          p_titel: n.titel,
          p_person: n.person ?? undefined,
          p_sprint: n.sprint ?? undefined,
          p_frist_datum: n.frist_datum ?? undefined,
          p_prioritaet: n.prioritaet,
          p_sichtbarkeit: n.sichtbarkeit,
          p_eltern: n.eltern ?? undefined,
          p_ressort: n.ressort ?? undefined,
          p_vorhaben: n.vorhaben ?? undefined,
          p_beschreibung: n.beschreibung ?? "",
          // Eine Person geht über p_person, damit Anlegen auch vor der Migration Stufe 2 klappt
          ...(n.personen && n.personen.length > 1 ? { p_personen: n.personen } : {}),
        });
        if (error) throw error;
        return data;
      },
      onSuccess: neu,
    }),
    bearbeiten: useMutation({
      mutationFn: ({ id, aenderung }: { id: string; aenderung: Record<string, Json> }) => rpc(db().rpc("aufgaben_bearbeiten", { p_id: id, p_aenderung: aenderung })),
      onMutate: ({ id, aenderung }) => sofort(id, () => aenderung as Aenderung),
      onError: zurueck,
      onSettled: neu,
    }),
    personHinzu: useMutation({
      mutationFn: ({ id, person }: { id: string; person: string }) => rpc(db().rpc("aufgaben_person_hinzufuegen", { p_id: id, p_person: person })),
      onMutate: ({ id, person }) => sofort(id, (a) => ({ personen: a.personen.includes(person) ? a.personen : [...a.personen, person] })),
      onError: zurueck,
      onSettled: neu,
    }),
    personWeg: useMutation({
      mutationFn: ({ id, person }: { id: string; person: string }) => rpc(db().rpc("aufgaben_person_entfernen", { p_id: id, p_person: person })),
      onMutate: ({ id, person }) => sofort(id, (a) => ({ personen: a.personen.filter((x) => x !== person) })),
      onError: zurueck,
      onSettled: neu,
    }),
    kommentieren: useMutation({
      mutationFn: async ({ id, text, erwaehnt }: { id: string; text: string; erwaehnt: string[] }): Promise<string> => {
        const { data, error } = await db().rpc("aufgaben_kommentar_schreiben", { p_aufgabe: id, p_text: text, p_erwaehnt: erwaehnt });
        if (error) throw error;
        return data;
      },
      onSettled: (_d, _e, { id }) => {
        void qc.invalidateQueries({ queryKey: ["kommentare", id] });
        void qc.invalidateQueries({ queryKey: ["verlauf", id] });
        void qc.invalidateQueries({ queryKey: ["folgen", id] });
      },
    }),
    kommentarLoeschen: useMutation({
      mutationFn: ({ kommentar }: { id: string; kommentar: string }) => rpc(db().rpc("aufgaben_kommentar_loeschen", { p_id: kommentar })),
      onSettled: (_d, _e, { id }) => {
        void qc.invalidateQueries({ queryKey: ["kommentare", id] });
        void qc.invalidateQueries({ queryKey: ["verlauf", id] });
      },
    }),
    zuweisen: useMutation({
      mutationFn: ({ id, person }: { id: string; person: string | null }) => rpc(db().rpc("aufgaben_zuweisen", { p_id: id, p_person: person as string })),
      onSettled: neu,
    }),
    uebernehmen: useMutation({ mutationFn: (id: string) => rpc(db().rpc("aufgaben_uebernehmen", { p_id: id })), onSettled: neu }),
    abgeben: useMutation({ mutationFn: (id: string) => rpc(db().rpc("aufgaben_abgeben", { p_id: id })), onSettled: neu }),
    /** Status sofort anzeigen (Dialog, Listen, Board). Die Rückfrage bei offenen Unteraufgaben kommt als Fehler P0003 und rollt zurück. */
    statusSofort: useMutation({
      mutationFn: ({ id, status, grund, unteraufgabenMit }: { id: string; status: Status; grund?: string; unteraufgabenMit?: boolean }) =>
        rpc(db().rpc("aufgaben_status_setzen", { p_id: id, p_status: status, p_grund: grund ?? "", p_unteraufgaben_mit: unteraufgabenMit })),
      onMutate: ({ id, status, grund }) => sofort(id, () => ({ status, ...(grund !== undefined ? { status_grund: grund } : {}) })),
      onError: zurueck,
      onSettled: neu,
    }),
    /** „Löschen“ (weich, in der Datenbank archivieren): Daten bleiben, Wiederherstellen geht. */
    archivieren: useMutation({ mutationFn: (id: string) => rpc(db().rpc("aufgaben_archivieren", { p_id: id })), onSettled: neu }),
    wiederherstellen: useMutation({ mutationFn: (id: string) => rpc(db().rpc("aufgaben_wiederherstellen", { p_id: id })), onSettled: neu }),
    inSprint: useMutation({ mutationFn: (id: string) => rpc(db().rpc("aufgaben_in_sprint", { p_id: id })), onSettled: neu }),
    inBacklog: useMutation({ mutationFn: (id: string) => rpc(db().rpc("aufgaben_in_backlog", { p_id: id })), onSettled: neu }),
    wiederOeffnen: useMutation({ mutationFn: (id: string) => rpc(db().rpc("aufgaben_wieder_oeffnen", { p_id: id })), onSettled: neu }),
    folgen: useMutation({
      mutationFn: ({ id, an }: { id: string; an: boolean }) => rpc(db().rpc("aufgaben_folgen_setzen", { p_id: id, p_folgen: an })),
      onSettled: () => void qc.invalidateQueries({ queryKey: ["folgen"] }),
    }),
  };
}

/** Fehler „offene Unteraufgaben“ aus aufgaben_status_setzen (Code P0003) */
export function offeneUnteraufgaben(e: unknown): string | null {
  const f = e as { code?: unknown; message?: unknown } | null;
  return f && f.code === "P0003" && typeof f.message === "string" ? f.message : null;
}
